import React, { useCallback, useMemo, useState, useEffect, useRef, memo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { IConfigStateGroups } from 'constants/interfaces'
import { FixedSizeList as List } from 'react-window'
import { TreeNode } from './TreeNode'
import { FlatNode, useFlatTree } from './hooks/useGroupsTree'
import useSelection, { SelectionMode } from './hooks/useSelection'
import { SearchInput } from 'components/TasksV2/Insights/Content/ContextsList/SearchInput'
import { NoFocusButton } from '../NoFocusButton'
import { NoResults } from '../GroupsTree/NoResults'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'

const ROW_HEIGHT = 60

interface Props {
  groups: IConfigStateGroups
  allowedGroups: string[]
  selectionMode?: SelectionMode
  showSearch?: boolean
  listHeight?: number
  initialSelectedIds?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
  isShowTags?: boolean
  isCloseMode?: boolean
}

const OptimizedGroupsTree: React.FC<Props> = memo(
  ({
    groups,
    allowedGroups,
    showSearch = true,
    selectionMode = SelectionMode.MULTIPLE,
    listHeight = 600,
    initialSelectedIds = [],
    onSelectionChange,
    isShowTags = false,
    isCloseMode = false,
  }) => {
    const isRtl = useSelector(isRtlSelector)
    const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null)
    const needsScrollRef = useRef<boolean>(false)
    const t_select_all = useLocaleText('select_all_stores_button')
    const t_clear = useLocaleText('clear_stores_button')

    const {
      visibleNodes,
      toggleNode,
      openNodes,
      searchTerm,
      searchResult,
      handleSearch,
      listRef,
      calculateIndeterminateState,
      getChildrenIds,
      expandToNode,
      clearSearch,
    } = useFlatTree(groups, allowedGroups, selectionMode, isCloseMode)

    const selectionOptions = useMemo(
      () => ({
        mode: selectionMode,
        initialSelectedIds,
        allIds: allowedGroups,
        getChildrenIds,
        onSelectionChange,
      }),
      [selectionMode, initialSelectedIds, allowedGroups, getChildrenIds, onSelectionChange]
    )

    const { toggleSelection, isSelected, selectedIds, clearSelection, isAllSelected, selectAll } =
      useSelection(selectionOptions)

    const TreeRow = useMemo(() => {
      return ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const node = visibleNodes[index]
        const isOpen = openNodes.has(node.oid)
        const nodeIsSelected = isSelected(node.oid)
        const isIndeterminate = calculateIndeterminateState(node, new Set(selectedIds))
        const isHighlighted = node.oid === highlightedNodeId

        return (
          <TreeNode
            node={node}
            isOpen={isOpen}
            isIndeterminate={isIndeterminate}
            isSelected={nodeIsSelected}
            toggleSelection={toggleSelection}
            toggleNode={toggleNode}
            style={style}
            isHighlighted={isHighlighted}
            isShowTags={isShowTags}
            selectionMode={selectionMode}
          />
        )
      }
    }, [
      visibleNodes,
      openNodes,
      isSelected,
      calculateIndeterminateState,
      selectedIds,
      highlightedNodeId,
      toggleSelection,
      toggleNode,
      isShowTags,
      selectionMode,
    ])

    const toggleSelectAll = useCallback(() => {
      if (isAllSelected) {
        clearSelection()
      } else {
        selectAll()
      }
    }, [isAllSelected, selectAll, clearSelection])

    const handleSearchResultClick = useCallback(
      (node: FlatNode) => {
        clearSearch()
        expandToNode(node.oid)
        setHighlightedNodeId(node.oid)

        needsScrollRef.current = true
      },
      [clearSearch, expandToNode]
    )

    useEffect(() => {
      if (needsScrollRef.current && highlightedNodeId) {
        const nodeIndex = visibleNodes.findIndex((n) => n.oid === highlightedNodeId)

        if (nodeIndex !== -1 && listRef.current) {
          requestAnimationFrame(() => {
            listRef.current?.scrollToItem(nodeIndex, 'center')

            needsScrollRef.current = false
          })
        }
      }
    }, [visibleNodes, highlightedNodeId, listRef])

    return (
      <Box>
        {showSearch && (
          <Flex w="100%" flexDir="row" alignItems="center" p={2} justifyContent="space-between" style={{ gap: 6 }}>
            <SearchInput
              w="100%"
              borderRadius="5px"
              border="1px solid #e0e0e0"
              onChange={handleSearch}
              value={searchTerm}
            />
            {selectionMode === SelectionMode.MULTIPLE && !searchTerm && (
              <NoFocusButton
                w="max-content"
                minWidth="100px"
                onClick={toggleSelectAll}
                borderRadius="5px"
                fontWeight="normal"
                borderColor={colors.greyLight}
              >
                {isAllSelected ? t_clear : t_select_all}
              </NoFocusButton>
            )}
          </Flex>
        )}

        {searchTerm ? (
          <>
            <Box overflowY="auto" h={listHeight}>
              {searchResult.length > 0 &&
                searchResult.map((node) => (
                  <Box key={node.oid} p={2} cursor="pointer" onClick={() => handleSearchResultClick(node)}>
                    <Flex alignItems="center" justifyContent="space-between">
                      <Box>
                        <Text fontWeight="bold">{node.name}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {node.path.join(' → ')}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              {searchResult.length === 0 && <NoResults />}
            </Box>
          </>
        ) : (
          <Box>
            <List
              ref={listRef}
              direction={isRtl ? 'rtl' : 'ltr'}
              height={listHeight}
              itemCount={visibleNodes.length}
              itemSize={ROW_HEIGHT}
              width="100%"
            >
              {TreeRow}
            </List>
          </Box>
        )}
      </Box>
    )
  }
)

export default OptimizedGroupsTree
